import { useEffect } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { decodePeek, recordPath, formatMoney, formatPhone, toCents, type Peekable } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { usePreferences } from '../lib/preferences.js';
import { Timeline, type TimelineEvent } from './Timeline.js';
import { RecordLink } from './RecordLink.js';

/**
 * The side panel.
 *
 * Answers the request directly: you are working on something, you need to see a customer,
 * and you should not have to leave the screen you are on to do it.
 *
 * Two properties make it worth having rather than being a modal:
 *
 *   - **The page behind stays live.** No scrim swallowing clicks, no scroll lock. You can
 *     read the customer and keep typing in the job you were editing.
 *   - **The target is in the URL** (`?peek=customer:<id>`), so a reload restores it and the
 *     back button closes it, like everything else in this interface.
 *
 * It shows a summary and the timeline, not a second copy of the full record. Anything more
 * and it becomes a worse version of the page it is trying to save you from opening.
 */

type PeekSummary = {
  kind: Peekable;
  id: string;
  title: string;
  subtitle: string | null;
  facts: Array<{ label: string; value: string; tone?: 'default' | 'money' | 'due' | 'phone' }>;
  /** Actions specific to this record kind, e.g. call the customer. */
  phone?: string | null;
};

export function PeekPanel() {
  const navigate = useNavigate();
  const { preferences } = usePreferences();
  const raw = useRouterState({ select: (s) => (s.location.search as { peek?: string }).peek });
  const target = decodePeek(raw);

  const close = () => {
    void navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, peek: undefined }), replace: true });
  };

  // Escape closes it. Registered on the document because focus is usually still in the page
  // behind — which is the whole point of the panel.
  useEffect(() => {
    if (!target) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [target?.kind, target?.id]);

  const summary = useQuery({
    queryKey: [target?.kind ?? 'peek', target?.id, 'peek'],
    queryFn: () => request<PeekSummary>(`/${pluralise(target!.kind)}/${target!.id}/summary`),
    enabled: Boolean(target),
  });

  const timeline = useQuery({
    queryKey: [target?.kind ?? 'peek', target?.id, 'timeline'],
    queryFn: () => request<{ items: TimelineEvent[] }>(`/${pluralise(target!.kind)}/${target!.id}/timeline?limit=60`),
    // Only customers and jobs have a story worth telling; a payment is a single event.
    enabled: Boolean(target && (target.kind === 'customer' || target.kind === 'job')),
  });

  if (!target) return null;

  const data = summary.data;

  return (
    <aside
      className={`peek peek--${preferences.peekSide}`}
      role="complementary"
      aria-label={data?.title ?? 'Aperçu'}
    >
      <header className="peek__head">
        <div className="peek__identity">
          <span className="label">{KIND_LABEL[target.kind]}</span>
          <h2 className="peek__title">{data?.title ?? '…'}</h2>
          {data?.subtitle && <p className="peek__subtitle">{data.subtitle}</p>}
        </div>

        <div className="peek__controls">
          {/* Escaping to the full record must always be one click away, or the panel becomes
              a dead end when it turns out you did need the whole page. */}
          <RecordLink kind={target.kind} id={target.id} mode="page" className="peek__expand">
            Ouvrir la fiche
          </RecordLink>
          <button type="button" className="peek__close" onClick={close} aria-label="Fermer l'aperçu">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
      </header>

      {summary.isLoading && <p className="peek__loading">Chargement…</p>}

      {summary.isError && (
        <p className="peek__error">
          Impossible de charger cette fiche.
          <button type="button" onClick={() => summary.refetch()}>
            Réessayer
          </button>
        </p>
      )}

      {data && (
        <>
          <dl className="peek__facts">
            {data.facts.map((fact) => (
              <div key={fact.label} className="peek__fact">
                <dt className="label">{fact.label}</dt>
                <dd className={factClass(fact.tone)}>
                  {fact.tone === 'money' || fact.tone === 'due'
                    ? formatMoney(toCents(fact.value), 'fr')
                    : fact.tone === 'phone'
                      ? formatPhone(fact.value)
                      : fact.value}
                </dd>
              </div>
            ))}
          </dl>

          {data.phone && (
            <a className="peek__call" href={`tel:${data.phone.replace(/\D/g, '')}`}>
              Appeler {formatPhone(data.phone)}
            </a>
          )}

          {(target.kind === 'customer' || target.kind === 'job') && (
            <section className="peek__timeline">
              <header className="peek__section label">Historique</header>
              <Timeline events={timeline.data?.items ?? []} loading={timeline.isLoading} />
            </section>
          )}
        </>
      )}
    </aside>
  );
}

const KIND_LABEL: Record<Peekable, string> = {
  customer: 'Client',
  job: 'Travail',
  quote: 'Soumission',
  invoice: 'Facture',
  appointment: 'Rendez-vous',
  payment: 'Paiement',
};

function factClass(tone: PeekSummary['facts'][number]['tone']): string {
  if (tone === 'money') return 'peek__value money';
  if (tone === 'due') return 'peek__value money money--due';
  if (tone === 'phone') return 'peek__value phone';
  return 'peek__value';
}

/** Record kind to its API collection. Kept here so route names live in one place. */
function pluralise(kind: Peekable): string {
  switch (kind) {
    case 'customer':
      return 'customers';
    case 'job':
      return 'jobs';
    case 'quote':
      return 'quotes';
    case 'invoice':
      return 'invoices';
    case 'payment':
      return 'payments';
    case 'appointment':
      return 'appointments';
  }
}

export { recordPath };
